import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Circle } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, Filter, X, DollarSign, Users, Bed, MapPin, Sparkles, Trash2, CircleDot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePropertyTranslation } from '@/hooks/usePropertyTranslation';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Property Popup component with translation
const PropertyPopup = ({ property, markerColor }) => {
  const { i18n } = useTranslation();
  const { translatedTitle, isLoading } = usePropertyTranslation(property, i18n.language);
  const displayTitle = translatedTitle || property.title;
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  
  const texts = {
    pt: { perNight: '/noite', viewDetails: 'Ver detalhes →' },
    en: { perNight: '/night', viewDetails: 'View details →' }
  };
  const t = texts[language];
  
  return (
    <div className="p-2 min-w-[200px]">
      {property.images && property.images.length > 0 && (
        <img
          src={property.images[0].image_url || property.images[0].url}
          alt={displayTitle}
          className="w-full h-24 object-cover rounded-lg mb-2"
        />
      )}
      <h3 className="font-semibold text-sm line-clamp-2">
        {isLoading ? (
          <span className="animate-pulse bg-gray-200 rounded inline-block w-full h-4"></span>
        ) : displayTitle}
      </h3>
      <p className="text-xs text-gray-500 mt-1">
        {property.city}, {property.state}
      </p>
      <p className="font-bold mt-2" style={{ color: markerColor }}>
        ${(property.price || property.nightly_price_suggestion)}{t.perNight}
      </p>
      <a
        href={`/property/${property.id}`}
        className="text-xs font-medium mt-2 inline-block hover:underline"
        style={{ color: markerColor }}
      >
        {t.viewDetails}
      </a>
    </div>
  );
};

// Premium Hover Preview Card - Centered in viewport using overlay
const HoverPreviewCard = ({ property, onClose, onMouseEnter, onMouseLeave }) => {
  const { i18n } = useTranslation();
  const { translatedTitle, isLoading } = usePropertyTranslation(property, i18n.language);
  const displayTitle = translatedTitle || property.title;
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const markerColor = getMarkerColor(property.plan_type);
  
  const texts = {
    pt: { perNight: '/noite', viewDetails: 'Ver detalhes', guests: 'hóspedes', beds: 'quartos' },
    en: { perNight: '/night', viewDetails: 'View details', guests: 'guests', beds: 'beds' }
  };
  const t = texts[language];
  
  const isDiamond = property.plan_type === 'diamond';
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        pointerEvents: 'none'
      }}
    >
      <motion.div 
        className={`bg-white rounded-xl shadow-2xl overflow-hidden w-80 ${
          isDiamond ? 'ring-2 ring-amber-400 diamond-sparkle' : ''
        }`}
        style={{ pointerEvents: 'auto' }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.15 }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
      {/* Image */}
      <div className="relative h-40 overflow-hidden">
        {property.images && property.images.length > 0 ? (
          <img
            src={property.images[0].image_url || property.images[0].url}
            alt={displayTitle}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-gray-400" />
          </div>
        )}
        
        {/* Plan Badge */}
        {isDiamond && (
          <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            DIAMOND
          </div>
        )}
        
        {/* Price Tag */}
        <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg">
          <span className="text-xl font-bold" style={{ color: markerColor }}>
            ${(property.price || property.nightly_price_suggestion)}
          </span>
          <span className="text-xs text-gray-500 ml-1">{t.perNight}</span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-base line-clamp-2 text-gray-900">
          {isLoading ? (
            <span className="animate-pulse bg-gray-200 rounded inline-block w-full h-4"></span>
          ) : displayTitle}
        </h3>
        
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <MapPin className="w-3.5 h-3.5" />
          <span>{property.city}, {property.state}</span>
        </div>
        
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded">
            <Users className="w-3.5 h-3.5" />
            <span>{property.max_guests}</span>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded">
            <Bed className="w-3.5 h-3.5" />
            <span>{property.bedrooms}</span>
          </div>
        </div>
        
        <button
          onClick={() => window.location.href = `/property/${property.id}`}
          className="w-full text-center text-sm font-medium py-2.5 rounded-lg transition-colors hover:opacity-90"
          style={{ backgroundColor: markerColor, color: 'white' }}
        >
          {t.viewDetails} →
        </button>
      </div>
    </motion.div>
    </div>
  );
};

// Cluster Hover Preview Card - Shows multiple properties
const ClusterPreviewCard = ({ properties, onClose, onMouseEnter, onMouseLeave }) => {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  
  const texts = {
    pt: { perNight: '/noite', properties: 'imóveis', clickToExpand: 'Clique no cluster para expandir' },
    en: { perNight: '/night', properties: 'properties', clickToExpand: 'Click cluster to expand' }
  };
  const t = texts[language];
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        pointerEvents: 'none'
      }}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl overflow-hidden w-80"
        style={{ pointerEvents: 'auto' }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.15 }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Header */}
      <div className="bg-gradient-to-r from-[#00C2D1] to-[#00A8B5] px-4 py-3 text-white">
        <h3 className="font-semibold text-lg">{properties.length} {t.properties}</h3>
        <p className="text-sm text-white/80">{t.clickToExpand}</p>
      </div>
      
      {/* Properties List */}
      <div className="max-h-64 overflow-y-auto">
        {properties.slice(0, 5).map((property, idx) => {
          const markerColor = getMarkerColor(property.plan_type);
          const isDiamond = property.plan_type === 'diamond';
          
          return (
            <div 
              key={property.id} 
              className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                isDiamond ? 'bg-amber-50' : ''
              }`}
              onClick={() => window.location.href = `/property/${property.id}`}
            >
              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                {property.images && property.images.length > 0 ? (
                  <img
                    src={property.images[0].image_url || property.images[0].url}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-gray-400" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  {isDiamond && <Sparkles className="w-3 h-3 text-amber-500" />}
                  <p className="font-medium text-sm text-gray-900 truncate">{property.title}</p>
                </div>
                <p className="text-xs text-gray-500">{property.city}</p>
                <p className="font-bold text-sm" style={{ color: markerColor }}>
                  ${(property.price || property.nightly_price_suggestion)}<span className="font-normal text-gray-500">{t.perNight}</span>
                </p>
              </div>
            </div>
          );
        })}
        
        {properties.length > 5 && (
          <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
            +{properties.length - 5} {language === 'pt' ? 'mais' : 'more'}...
          </div>
        )}
      </div>
    </motion.div>
    </div>
  );
};

// Selected Property Card component with translation
const SelectedPropertyCard = ({ property, onClose }) => {
  const { i18n } = useTranslation();
  const { translatedTitle, isLoading } = usePropertyTranslation(property, i18n.language);
  const displayTitle = translatedTitle || property.title;
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const markerColor = getMarkerColor(property.plan_type);
  
  const texts = {
    pt: { perNight: '/noite', viewDetails: 'Ver detalhes →' },
    en: { perNight: '/night', viewDetails: 'View details →' }
  };
  const t = texts[language];
  
  return (
    <motion.div 
      className="absolute bottom-4 left-4 right-4 bg-white rounded-xl shadow-2xl p-4 max-w-sm z-[1000]" 
      style={{ borderColor: markerColor, borderWidth: '3px' }}
      data-testid="map-property-preview"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      <div className="flex items-start gap-3">
        {property.images && property.images.length > 0 && (
          <img
            src={property.images[0].image_url || property.images[0].url}
            alt={displayTitle}
            className="w-24 h-24 object-cover rounded-lg"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm line-clamp-2 text-[#08172F]">
            {isLoading ? (
              <span className="animate-pulse bg-gray-200 rounded inline-block w-full h-4"></span>
            ) : displayTitle}
          </h3>
          <p className="text-xs text-[#666666] mt-0.5">
            {property.city}, {property.state}
          </p>
          <p 
            className="font-bold mt-2 text-base"
            style={{ color: markerColor }}
          >
            ${(property.price || property.nightly_price_suggestion)}{t.perNight}
          </p>
          <button
            onClick={() => window.location.href = `/property/${property.id}`}
            className="text-xs font-medium mt-1 inline-flex items-center gap-1 hover:underline"
            style={{ color: markerColor }}
            data-testid="preview-view-details-button"
          >
            {t.viewDetails}
          </button>
        </div>
        <button
          onClick={onClose}
          className="text-[#666666] hover:text-[#08172F] font-bold text-lg leading-none"
          data-testid="preview-close-button"
        >
          ✕
        </button>
      </div>
    </motion.div>
  );
};

// Price Filter Component
const PriceFilterPanel = ({ priceRange, setPriceRange, maxPrice, isOpen, onToggle, language }) => {
  const texts = {
    pt: { priceFilter: 'Filtro de Preço', perNight: '/noite', apply: 'Aplicar' },
    en: { priceFilter: 'Price Filter', perNight: '/night', apply: 'Apply' }
  };
  const t = texts[language];
  
  return (
    <div className="leaflet-top leaflet-right" style={{ top: '10px', right: '10px' }}>
      <div className="leaflet-control">
        <motion.div 
          className="bg-white rounded-xl shadow-lg overflow-hidden"
          initial={false}
          animate={{ width: isOpen ? 280 : 'auto' }}
        >
          <button
            onClick={onToggle}
            className="flex items-center gap-2 px-4 py-3 w-full hover:bg-gray-50 transition-colors"
            data-testid="price-filter-toggle"
          >
            <DollarSign className="w-4 h-4 text-[#00C2D1]" />
            <span className="font-medium text-sm text-gray-700">{t.priceFilter}</span>
            {isOpen ? <X className="w-4 h-4 ml-auto" /> : <Filter className="w-4 h-4 ml-auto" />}
          </button>
          
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="px-4 pb-4 space-y-4"
              >
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">${priceRange[0]}</span>
                  <span className="text-gray-600">${priceRange[1]}</span>
                </div>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  min={0}
                  max={maxPrice}
                  step={10}
                  className="w-full"
                />
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">
                    ${priceRange[0]} - ${priceRange[1]}{t.perNight}
                  </Badge>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

// Zip code to approximate coordinates mapping for Orlando area
const ZIP_CODE_COORDS = {
  '32801': { lat: 28.5383, lng: -81.3792 },
  '32802': { lat: 28.5383, lng: -81.3792 },
  '32803': { lat: 28.5564, lng: -81.3545 },
  '32819': { lat: 28.4614, lng: -81.4689 },
  '32821': { lat: 28.3814, lng: -81.4989 },
  '32824': { lat: 28.3614, lng: -81.3889 },
  '32830': { lat: 28.3852, lng: -81.5639 },
  '32836': { lat: 28.4214, lng: -81.5189 },
  '32837': { lat: 28.3814, lng: -81.4289 },
  '33896': { lat: 28.2880, lng: -81.5050 },
  '34741': { lat: 28.2919, lng: -81.4076 },
  '34742': { lat: 28.2919, lng: -81.4076 },
  '34743': { lat: 28.3219, lng: -81.3576 },
  '34744': { lat: 28.3019, lng: -81.4276 },
  '34745': { lat: 28.2619, lng: -81.4376 },
  '34746': { lat: 28.2419, lng: -81.4576 },
  '34747': { lat: 28.3219, lng: -81.5876 },
  '34758': { lat: 28.2019, lng: -81.5276 },
  '34759': { lat: 28.1519, lng: -81.5076 },
};

// Custom marker icon with warm colors
const createCustomIcon = (color, price, isHovered = false, isDiamond = false) => {
  const scale = isHovered ? 1.2 : 1;
  const shadow = isHovered ? '0 6px 12px rgba(0,0,0,0.4)' : '0 4px 6px rgba(0,0,0,0.3)';
  const diamondGlow = isDiamond ? '0 0 10px rgba(255, 200, 87, 0.5)' : '';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; transform: scale(${scale}); transition: transform 0.2s ease;">
        <div style="
          background: ${isDiamond ? 'linear-gradient(135deg, #FFE9A3 0%, #FFC857 50%, #FFE9A3 100%)' : color};
          color: ${isDiamond ? '#0B1220' : 'white'};
          padding: 6px 10px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: bold;
          box-shadow: ${shadow}${diamondGlow ? ', ' + diamondGlow : ''};
          border: 2px solid ${isDiamond ? '#FFC857' : 'white'};
          white-space: nowrap;
        ">
          ${isDiamond ? '💎 ' : ''}$${price}
        </div>
        <div style="
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 10px solid ${isDiamond ? '#FFC857' : color};
          margin-top: -2px;
        "></div>
      </div>
    `,
    iconSize: [70, 50],
    iconAnchor: [35, 50],
    popupAnchor: [0, -50],
  });
};

// Warm colors for markers based on plan type
const getMarkerColor = (planType, index = 0) => {
  const warmColors = [
    '#FF6B35', // Bright Orange
    '#F7931E', // Orange
    '#FFB627', // Golden Yellow
    '#FF5733', // Red Orange
    '#FF8C42', // Light Orange
    '#E85D04', // Deep Orange
    '#DC2F02', // Red
    '#FAA307', // Amber
  ];
  
  switch (planType) {
    case 'diamond': return '#FFC857';
    case 'premium': return '#8B5CF6';
    case 'gold': return '#F59E0B';
    case 'silver': return '#6B7280';
    default: return warmColors[index % warmColors.length];
  }
};

// Create custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let bgColor = '#00C2D1';
  
  if (count >= 10) {
    size = 'large';
    bgColor = '#FF6B35';
  } else if (count >= 5) {
    size = 'medium';
    bgColor = '#F7931E';
  }
  
  const sizeMap = {
    small: { width: 40, height: 40, fontSize: 14 },
    medium: { width: 50, height: 50, fontSize: 16 },
    large: { width: 60, height: 60, fontSize: 18 },
  };
  
  const { width, height, fontSize } = sizeMap[size];
  
  return L.divIcon({
    html: `
      <div style="
        background: linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%);
        width: ${width}px;
        height: ${height}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${fontSize}px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        border: 3px solid white;
        transition: transform 0.2s ease;
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-marker',
    iconSize: L.point(width, height, true),
  });
};

// Component to fit bounds to all markers
const FitBounds = ({ bounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [map, bounds]);
  
  return null;
};

// Component to handle fit all button
const FitAllButton = ({ bounds, language }) => {
  const map = useMap();
  const texts = {
    pt: { viewAll: 'Ver Todos' },
    en: { viewAll: 'View All' }
  };
  const t = texts[language];
  
  const handleFitAll = () => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], duration: 1 });
    }
  };
  
  return (
    <div className="leaflet-top leaflet-left" style={{ top: '10px', left: '46px' }}>
      <div className="leaflet-control leaflet-bar">
        <button
          onClick={handleFitAll}
          className="bg-white hover:bg-gray-100 text-[#08172F] font-medium px-3 py-2 rounded shadow-md flex items-center gap-2 text-sm transition-colors border border-gray-300"
          data-testid="fit-all-properties-button"
          title={t.viewAll}
        >
          <Maximize2 className="w-4 h-4" />
          <span className="hidden sm:inline">{t.viewAll}</span>
        </button>
      </div>
    </div>
  );
};

// Drawing Control Panel component - Circle radius filter
const DrawControlPanel = ({ isDrawMode, onToggleDrawMode, hasCircle, onClear, radius, onRadiusChange, language }) => {
  const texts = {
    pt: { 
      drawCircle: 'Filtrar por Área',
      clearArea: 'Limpar Área',
      drawHint: 'Clique no mapa',
      radius: 'Raio',
      km: 'km'
    },
    en: { 
      drawCircle: 'Filter by Area',
      clearArea: 'Clear Area',
      drawHint: 'Click on map',
      radius: 'Radius',
      km: 'km'
    }
  };
  const t = texts[language];
  
  return (
    <div className="leaflet-top leaflet-left" style={{ top: '100px', left: '10px' }}>
      <div className="leaflet-control">
        <div className="bg-white rounded shadow-md overflow-hidden border border-gray-300" style={{ minWidth: '140px' }}>
          {hasCircle ? (
            <button
              onClick={onClear}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full"
              data-testid="clear-area-button"
            >
              <Trash2 className="w-4 h-4" />
              {t.clearArea}
            </button>
          ) : (
            <>
              <button
                onClick={onToggleDrawMode}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors w-full ${
                  isDrawMode 
                    ? 'bg-[#00C2D1] text-white' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="draw-area-button"
              >
                <CircleDot className="w-4 h-4" />
                {isDrawMode ? t.drawHint : t.drawCircle}
              </button>
              {isDrawMode && (
                <div className="px-3 py-2 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>{t.radius}</span>
                    <span>{radius} {t.km}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={radius}
                    onChange={(e) => onRadiusChange(parseInt(e.target.value))}
                    className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00C2D1]"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Component to handle map clicks for circle drawing
const MapClickHandler = ({ enabled, onClick }) => {
  const map = useMap();
  
  useMapEvents({
    click: (e) => {
      if (enabled) {
        // Prevent click from being handled by other elements
        e.originalEvent.stopPropagation();
        onClick(e.latlng);
      }
    }
  });
  
  // Change cursor when in draw mode
  useEffect(() => {
    const container = map.getContainer();
    if (enabled) {
      container.style.cursor = 'crosshair';
    } else {
      container.style.cursor = '';
    }
    return () => {
      container.style.cursor = '';
    };
  }, [enabled, map]);
  
  return null;
};

const MapView = ({ 
  properties = [], 
  onPropertyClick, 
  onPolygonChange,
  initialViewState = { latitude: 28.3852, longitude: -81.5639, zoom: 10 },
  enableDrawing = false
}) => {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [hoveredClusterProperties, setHoveredClusterProperties] = useState(null);
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [showPriceFilter, setShowPriceFilter] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  
  // Circle filter state
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(10); // km
  
  // Calculate max price from properties
  const maxPrice = useMemo(() => {
    if (properties.length === 0) return 1000;
    return Math.max(...properties.map(p => p.nightly_price_suggestion || 0), 1000);
  }, [properties]);
  
  // Initialize price range with max price
  useEffect(() => {
    setPriceRange([0, maxPrice]);
  }, [maxPrice]);

  // Handle map click for circle filter
  const handleMapClick = useCallback((latlng) => {
    setCircleCenter(latlng);
    setIsDrawMode(false);
    
    // Notify parent with a circular "polygon" (approximated)
    if (onPolygonChange) {
      const circleGeoJSON = {
        type: 'Circle',
        center: [latlng.lng, latlng.lat],
        radius: circleRadius * 1000 // Convert km to meters
      };
      onPolygonChange(circleGeoJSON);
    }
  }, [circleRadius, onPolygonChange]);

  // Clear circle filter
  const clearCircle = useCallback(() => {
    setCircleCenter(null);
    setIsDrawMode(false);
    if (onPolygonChange) {
      onPolygonChange(null);
    }
  }, [onPolygonChange]);

  // Check if a point is inside the circle using Haversine formula
  const isPointInCircle = useCallback((lat, lng) => {
    if (!circleCenter) return true;
    
    const R = 6371; // Earth's radius in km
    const dLat = (lat - circleCenter.lat) * Math.PI / 180;
    const dLon = (lng - circleCenter.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(circleCenter.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance <= circleRadius;
  }, [circleCenter, circleRadius]);

  // Process properties to handle same coordinates and missing coordinates
  const processedProperties = useMemo(() => {
    const coordMap = new Map();
    
    // Filter by price range
    const filteredProperties = properties.filter(p => {
      const price = p.nightly_price_suggestion || p.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    return filteredProperties.map((property, index) => {
      let lat = property.latitude;
      let lng = property.longitude;
      
      // If no coordinates, try to get from zip code
      if (!lat || !lng) {
        const zipCode = property.zip_code?.toString().trim();
        if (zipCode && ZIP_CODE_COORDS[zipCode]) {
          lat = ZIP_CODE_COORDS[zipCode].lat;
          lng = ZIP_CODE_COORDS[zipCode].lng;
        } else if (property.city) {
          lat = 28.3852;
          lng = -81.5639;
        }
      }
      
      if (!lat || !lng) return { ...property, _hasCoords: false };

      // Check if point is within circle filter (if any)
      if (circleCenter && !isPointInCircle(lat, lng)) {
        return { ...property, _hasCoords: false };
      }
      
      // Check for duplicate coordinates and offset if needed
      const coordKey = `${lat.toFixed(2)},${lng.toFixed(2)}`;
      if (coordMap.has(coordKey)) {
        const count = coordMap.get(coordKey);
        coordMap.set(coordKey, count + 1);
        
        // Offset markers in a circular pattern
        const offsetDistance = 0.015; // ~1.5km offset for visibility
        const angle = (count * 72) * (Math.PI / 180);
        lat = lat + (offsetDistance * Math.cos(angle));
        lng = lng + (offsetDistance * Math.sin(angle));
      } else {
        coordMap.set(coordKey, 1);
      }
      
      return {
        ...property,
        _lat: lat,
        _lng: lng,
        _hasCoords: true,
        _index: index
      };
    });
  }, [properties, priceRange, circleCenter, isPointInCircle]);

  // Get bounds for all properties
  const bounds = useMemo(() => {
    const validProperties = processedProperties.filter(p => p._hasCoords);
    if (validProperties.length === 0) return null;
    return validProperties.map(p => [p._lat, p._lng]);
  }, [processedProperties]);

  // Calculate center
  const center = useMemo(() => {
    const validProperties = processedProperties.filter(p => p._hasCoords);
    if (validProperties.length === 0) {
      return [initialViewState.latitude, initialViewState.longitude];
    }
    const lats = validProperties.map(p => p._lat);
    const lngs = validProperties.map(p => p._lng);
    return [
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2
    ];
  }, [processedProperties, initialViewState]);

  const handleMarkerHover = useCallback((property) => {
    setHoveredProperty(property);
  }, []);

  return (
    <div className="w-full h-full relative" data-testid="map-view">
      <MapContainer
        center={center}
        zoom={initialViewState.zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        
        {bounds && <FitBounds bounds={bounds} />}
        <FitAllButton bounds={bounds} language={language} />
        <PriceFilterPanel 
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          maxPrice={maxPrice}
          isOpen={showPriceFilter}
          onToggle={() => setShowPriceFilter(!showPriceFilter)}
          language={language}
        />

        {/* Map click handler for circle drawing */}
        <MapClickHandler enabled={isDrawMode} onClick={handleMapClick} />

        {/* Circle visualization */}
        {circleCenter && (
          <Circle 
            center={[circleCenter.lat, circleCenter.lng]}
            radius={circleRadius * 1000} // Convert km to meters
            pathOptions={{
              color: '#00C2D1',
              fillColor: '#00C2D1',
              fillOpacity: 0.15,
              weight: 2
            }}
          />
        )}

        {/* Drawing Control Panel */}
        {enableDrawing && (
          <DrawControlPanel 
            isDrawMode={isDrawMode}
            onToggleDrawMode={() => setIsDrawMode(!isDrawMode)}
            hasCircle={!!circleCenter}
            onClear={clearCircle}
            radius={circleRadius}
            onRadiusChange={setCircleRadius}
            language={language}
          />
        )}
        
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={1}
          disableClusteringAtZoom={1}
          spiderfyOnMaxZoom={true}
          spiderfyDistanceMultiplier={3}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          eventHandlers={{
            clustermouseover: (e) => {
              const cluster = e.propagatedFrom || e.layer;
              if (cluster && cluster.getAllChildMarkers) {
                const childMarkers = cluster.getAllChildMarkers();
                const clusterProperties = childMarkers.map(marker => {
                  const latLng = marker.getLatLng();
                  return processedProperties.find(p => 
                    p._lat === latLng.lat && p._lng === latLng.lng
                  );
                }).filter(Boolean);
                setHoveredClusterProperties(clusterProperties);
                setHoveredProperty(null);
              }
            },
            clustermouseout: () => {
              setTimeout(() => {
                if (!isHoveringCard) {
                  setHoveredClusterProperties(null);
                }
              }, 100);
            },
            clusterclick: () => {
              setHoveredClusterProperties(null);
            }
          }}
        >
          {processedProperties.map((property, index) => {
            if (!property._hasCoords) return null;
            
            const isDiamond = property.plan_type === 'diamond';
            const isHovered = hoveredProperty?.id === property.id;
            const markerColor = getMarkerColor(property.plan_type, index);
            const customIcon = createCustomIcon(markerColor, (property.price || property.nightly_price_suggestion), isHovered, isDiamond);
            
            return (
              <Marker
                key={property.id}
                position={[property._lat, property._lng]}
                icon={customIcon}
                eventHandlers={{
                  click: () => {
                    setSelectedProperty(property);
                    setHoveredProperty(null);
                  },
                  mouseover: () => handleMarkerHover(property),
                  mouseout: () => {
                    setTimeout(() => {
                      if (!isHoveringCard) {
                        setHoveredProperty(null);
                      }
                    }, 100);
                  },
                }}
              />
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Hover Preview - Single Property */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {hoveredProperty && !selectedProperty && !hoveredClusterProperties && (
            <HoverPreviewCard 
              property={hoveredProperty}
              onClose={() => setHoveredProperty(null)}
              onMouseEnter={() => setIsHoveringCard(true)}
              onMouseLeave={() => {
                setIsHoveringCard(false);
                setHoveredProperty(null);
              }}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Hover Preview - Cluster Properties */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {hoveredClusterProperties && hoveredClusterProperties.length > 0 && !selectedProperty && (
            <ClusterPreviewCard 
              properties={hoveredClusterProperties}
              onClose={() => setHoveredClusterProperties(null)}
              onMouseEnter={() => setIsHoveringCard(true)}
              onMouseLeave={() => {
                setIsHoveringCard(false);
                setHoveredClusterProperties(null);
              }}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Selected Property Card */}
      <AnimatePresence>
        {selectedProperty && (
          <SelectedPropertyCard 
            property={selectedProperty} 
            onClose={() => setSelectedProperty(null)} 
          />
        )}
      </AnimatePresence>
      
      {/* Properties Count Badge */}
      <div className="absolute bottom-4 right-4 z-[1000]">
        <Badge className="bg-[#08172F] text-white px-3 py-1 text-sm shadow-lg">
          {processedProperties.filter(p => p._hasCoords).length} {language === 'pt' ? 'imóveis' : 'properties'}
        </Badge>
      </div>
    </div>
  );
};

export default MapView;
